import React, { useState } from 'react';
import { 
  ThumbsUp, 
  ThumbsDown, 
  Eye, 
  ExternalLink, 
  Download, 
  Play, 
  FileText, 
  Book,
  Trash2,
  MoreVertical,
  Calendar,
  User,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';

const ResourceCard = ({ 
  resource, 
  onLike, 
  onDislike, 
  onDelete, 
  onView,
  showActions = true 
}) => {
  const { currentUser } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const isOwner = currentUser && resource.submittedBy === currentUser.uid;
  const userInteraction = resource.userInteraction || 'none';

  // Get resource type icon
  const getResourceIcon = () => {
    switch (resource.resourceType) {
      case 'video':
        return <Play className="w-5 h-5 text-red-500" />;
      case 'pdf':
      case 'document':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'past papers':
        return <Book className="w-5 h-5 text-purple-500" />;
      default:
        return <BookOpen className="w-5 h-5 text-green-500" />;
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle resource click/view
  const handleResourceClick = () => {
    if (onView) {
      onView(resource.id);
    }

    // Open the resource link
    if (resource.link || resource.fileUrl) {
      window.open(resource.link || resource.fileUrl, '_blank');
    }
  };

  // Handle like
  const handleLike = (e) => {
    e.stopPropagation();
    if (onLike) {
      onLike(resource.id);
    }
  };

  // Handle dislike
  const handleDislike = (e) => {
    e.stopPropagation();
    if (onDislike) {
      onDislike(resource.id);
    }
  };

  // Handle delete
  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this resource?')) {
      if (onDelete) {
        onDelete(resource.id);
      }
    }
    setShowOptions(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      {/* Resource Header with Thumbnail */}
      <div className="relative">
        {resource.thumbnailUrl && !imageError ? (
          <div className="h-48 w-full overflow-hidden rounded-t-lg">
            <img
              src={resource.thumbnailUrl}
              alt={resource.title}
              className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
              onClick={handleResourceClick}
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div 
            className="h-32 w-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-t-lg flex items-center justify-center cursor-pointer hover:from-gray-200 hover:to-gray-300 transition-colors duration-200"
            onClick={handleResourceClick}
          >
            <div className="text-center">
              {getResourceIcon()}
              <p className="text-gray-500 text-sm mt-2">{resource.resourceType}</p>
            </div>
          </div>
        )}

        {/* Status Badge */}
        {resource.status === 'pending' && (
          <div className="absolute top-2 left-2">
            <span className="bg-yellow-500 text-white text-xs font-medium px-2 py-1 rounded-full">
              Pending Review
            </span>
          </div>
        )}

        {/* Options Menu for Owner */}
        {isOwner && showActions && (
          <div className="absolute top-2 right-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowOptions(!showOptions);
              }}
              className="bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1.5 transition-all duration-200"
            >
              <MoreVertical className="w-4 h-4 text-gray-600" />
            </button>
            
            {showOptions && (
              <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                <button
                  onClick={handleDelete}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center rounded-lg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resource Content */}
      <div className="p-4">
        {/* Title */}
        <h3 
          className="font-semibold text-gray-900 text-lg mb-2 cursor-pointer hover:text-green-600 transition-colors duration-200 line-clamp-2"
          onClick={handleResourceClick}
        >
          {resource.title}
        </h3>

        {/* Description */}
        {resource.description && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {resource.description}
          </p>
        )}

        {/* Subject and Type */}
        <div className="flex items-center space-x-4 mb-3">
          <div className="flex items-center">
            <BookOpen className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm font-medium text-gray-700">{resource.subject}</span>
          </div>
          <div className="flex items-center">
            {getResourceIcon()}
            <span className="text-sm text-gray-500 ml-1 capitalize">{resource.resourceType}</span>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(resource.createdAt)}
            </div>
            <div className="flex items-center">
              <Eye className="w-3 h-3 mr-1" />
              {resource.views || 0}
            </div>
          </div>
        </div>

        {/* University Information */}
        {(resource.university || resource.submittedByUniversity) && (
          <div className="mb-3">
            {resource.university === 'All Universities' || resource.applicableToAllUniversities ? (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                <User className="w-3 h-3 mr-1" />
                All Universities
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                <User className="w-3 h-3 mr-1" />
                {resource.university || resource.submittedByUniversity}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-4">
              {/* Like Button */}
              <button
                onClick={handleLike}
                className={`flex items-center space-x-1 text-sm transition-colors duration-200 ${
                  userInteraction === 'liked'
                    ? 'text-green-600'
                    : 'text-gray-500 hover:text-green-600'
                }`}
              >
                <ThumbsUp 
                  className={`w-4 h-4 ${userInteraction === 'liked' ? 'fill-current' : ''}`} 
                />
                <span>{resource.likes || 0}</span>
              </button>

              {/* Dislike Button */}
              <button
                onClick={handleDislike}
                className={`flex items-center space-x-1 text-sm transition-colors duration-200 ${
                  userInteraction === 'disliked'
                    ? 'text-red-600'
                    : 'text-gray-500 hover:text-red-600'
                }`}
              >
                <ThumbsDown 
                  className={`w-4 h-4 ${userInteraction === 'disliked' ? 'fill-current' : ''}`} 
                />
                <span>{resource.dislikes || 0}</span>
              </button>
            </div>

            {/* Access Button */}
            <button
              onClick={handleResourceClick}
              className="flex items-center space-x-1 bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors duration-200"
            >
              {resource.resourceType === 'video' ? (
                <>
                  <Play className="w-4 h-4" />
                  <span>Watch</span>
                </>
              ) : resource.resourceType === 'pdf' || resource.resourceType === 'past papers' ? (
                <>
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  <span>View</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Click outside to close options */}
      {showOptions && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowOptions(false)}
        />
      )}
    </div>
  );
};

export default ResourceCard;